import {
  getPinyinData,
  getPinyinSyllableEntry,
  getPinyinSyllableTermOptions,
} from "@-/dev.improve.haniverse.assets.pinyin";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import {
  PINYIN_TONE_NAMES,
  type PinyinTone,
  tonelessSyllableForPinyinAssetLookup,
} from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin";
import { cn } from "@-/com.npmjs.cn";
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from "@-/dev.improve.haniverse.components.ui.popover";
import { useState, type ReactNode } from "react";

const TONE_MEMORY_AIDS = [
  {
    caption: "A high-quality, flat red carpet",
    className: "text-red-600",
    color: "Red",
    explanation: "high, flat tone, held steady",
    label: "1st Tone",
    tone: 1,
  },
  {
    caption: "A green plant rising from the ground",
    className: "text-green-600",
    color: "Green",
    explanation: "rising tone, like an English question (?)",
    label: "2nd Tone",
    tone: 2,
  },
  {
    caption: "Low ground filled with blue water",
    className: "text-blue-600",
    color: "Blue",
    explanation: "low tone, dipping before rising",
    label: "3rd Tone",
    tone: 3,
  },
  {
    caption: "Purple grapes falling from a grapevine",
    className: "text-purple-600",
    color: "Purple",
    explanation: "falling tone, like an English exclamation (!)",
    label: "4th Tone",
    tone: 4,
  },
] as const;

const BRACKET_CONTENT_REGEX = /\[([^\]]+)\]/;

const bracketAccentClassDict = {
  default: ["text-sky-400", "text-rose-400", "text-amber-300"] as const,
  highContrast: ["text-sky-500", "text-rose-500", "text-amber-500"] as const,
} as const;

function getTermBracketHighlight(firstBracketInner: string, displayedText: string): string | null {
  const key = tonelessSyllableForPinyinAssetLookup(firstBracketInner);
  const entry = getPinyinSyllableEntry(getPinyinData(), key);
  if (entry === undefined) {
    return null;
  }
  // A syllable may curate several interchangeable terms; the phrase was generated from one of them,
  // but the rendered phrase doesn't record which. Pull each option's bracketed phonetic span and
  // prefer the one that actually appears in the displayed text (case-insensitively) — that's the
  // option the phrase used. Fall back to the first option's span when none matches.
  const lowerDisplayed = displayedText.toLowerCase();
  let firstSpan: string | null = null;
  for (const term of getPinyinSyllableTermOptions(entry)) {
    const span = BRACKET_CONTENT_REGEX.exec(term)?.[1];
    if (span === undefined) {
      continue;
    }
    firstSpan ??= span;
    if (lowerDisplayed.includes(span.toLowerCase())) {
      return span;
    }
  }
  return firstSpan;
}

function BracketSegmentText({
  accentClass,
  boldSubstring,
  renderText,
  text,
}: {
  accentClass: string;
  boldSubstring: string | null;
  renderText: (text: string) => ReactNode;
  text: string;
}) {
  if (!boldSubstring) {
    return <Text className={accentClass}>{renderText(text)}</Text>;
  }

  const lowerText = text.toLowerCase();
  const lowerBold = boldSubstring.toLowerCase();
  const index = lowerText.indexOf(lowerBold);
  if (index === -1) {
    return <Text className={accentClass}>{renderText(text)}</Text>;
  }

  const spanLen = lowerBold.length;
  const before = text.slice(0, index);
  const emphasized = text.slice(index, index + spanLen);
  const after = text.slice(index + spanLen);

  return (
    <Text className={accentClass}>
      {renderText(before)}
      <Text className={cn(accentClass, "font-bold underline")}>{renderText(emphasized)}</Text>
      {renderText(after)}
    </Text>
  );
}

export function PinyinMnemonicTextRenderer({
  accentVariant = "default",
  className,
  content,
  showToneHelp = false,
  toneNumbersPinyin,
}: {
  accentVariant?: keyof typeof bracketAccentClassDict;
  className?: string;
  content: string;
  /** Make authored tone-color words open a pronunciation memory aid. */
  showToneHelp?: boolean;
  /**
   * Reading key from `card.pinyinList` (e.g. `yi1`). Used to resolve `term` in `pinyin.toml` for
   * bolding inside the **second** `[…]` segment. Pass when the mnemonic does not start with the
   * syllable bracket (e.g. `[one] [yeast] …` instead of `[yi1][…]`).
   */
  toneNumbersPinyin?: string;
}) {
  const [selectedTone, setSelectedTone] = useState<PinyinTone>(4);
  const selectedAid =
    TONE_MEMORY_AIDS.find((aid) => aid.tone === selectedTone) ?? TONE_MEMORY_AIDS[3];
  const renderText = (text: string): ReactNode => {
    if (!showToneHelp) {
      return text;
    }
    return text.split(/(\b(?:red|green|blue|purple)\b)/giu).map((part, index) => {
      const aid = TONE_MEMORY_AIDS.find(
        (candidate) => candidate.color.toLowerCase() === part.toLowerCase(),
      );
      return aid === undefined ? (
        part
      ) : (
        <PopoverTrigger asChild key={index} onPress={() => setSelectedTone(aid.tone)}>
          <Text
            accessibilityHint="Explains this tone and its mnemonic"
            accessibilityLabel={`${part}: ${PINYIN_TONE_NAMES[aid.tone]} tone`}
            className={cn(aid.className, "underline")}
            testID={`mnemonic-tone-word-${aid.tone}`}
          >
            {part}
          </Text>
        </PopoverTrigger>
      );
    });
  };
  const bracketAccentClassList = bracketAccentClassDict[accentVariant];
  const parts = content.split(/(\[.*?\])/g);
  let bracketOrdinal = 0;
  let firstBracketInner: string | null = null;

  const mnemonicText = (
    <Text className={className}>
      {parts.map((part, index) => {
        if (part.startsWith("[") && part.endsWith("]")) {
          const text = part.slice(1, -1);
          const ordinal = bracketOrdinal;
          bracketOrdinal += 1;
          const accentClass = bracketAccentClassList[ordinal] ?? "text-inherit";

          if (ordinal === 0) {
            firstBracketInner = text;
          }

          if (ordinal === 1) {
            const termLookupSource =
              toneNumbersPinyin !== undefined && toneNumbersPinyin.trim().length > 0
                ? toneNumbersPinyin.trim()
                : (firstBracketInner ?? "");
            const highlight =
              termLookupSource.length > 0 ? getTermBracketHighlight(termLookupSource, text) : null;
            return (
              <BracketSegmentText
                accentClass={accentClass}
                boldSubstring={highlight}
                key={index}
                renderText={renderText}
                text={text}
              />
            );
          }

          return (
            <Text className={accentClass} key={index}>
              {renderText(text)}
            </Text>
          );
        }
        return (
          <Text className="text-inherit" key={index}>
            {renderText(part)}
          </Text>
        );
      })}
    </Text>
  );
  return showToneHelp ? (
    <Popover>
      {mnemonicText}
      <PopoverContent className="gap-2" testID="mnemonic-tone-popover">
        <Text className="text-sm">
          <Text className={cn(selectedAid.className, "font-semibold")}>{selectedAid.color}: </Text>
          {selectedAid.explanation}
        </Text>
        {TONE_MEMORY_AIDS.map((aid) => (
          <Text className="text-sm" key={aid.tone}>
            <Text className={cn(aid.className, "italic")}>{aid.label}: </Text>
            {aid.caption}
          </Text>
        ))}
        <PopoverClose
          accessibilityLabel="Close tone explanation"
          className="self-end px-2 py-1"
          testID="mnemonic-tone-popover-close"
        >
          <Text className="text-sm font-semibold">Close</Text>
        </PopoverClose>
      </PopoverContent>
    </Popover>
  ) : (
    mnemonicText
  );
}
